import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  DataGridPro,
  GridPaginationModel,
  GridRowParams,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { UIContext } from '../../context/UIContext';
import { OBJECT_KEY_ID } from '../../constants';
import { FilterContext } from '../../context/FilterContext';
import { DataGridConfigContext } from '../../context/DataGridConfigContext';
import { DataGridContext } from '../../context/DataGridContext';
import NoRows from './NoRows';

const DesktopDataGrid = <T, _TErrors>() => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isTablet, isMediumDevice } = useContext(UIContext);
  const {
    data,
    metadata,
    isLoading,
    setGridApiRef,
    translationKey,
    setErrorMessage,
    BuildColumns,
    showColumnsButton,
    columnVisibilityModel,
  } = useContext(DataGridConfigContext);
  const { processRowUpdate, rowStartEdit } = useContext(DataGridContext);
  const { paginationModel, setPaginationModel } = useContext(FilterContext);
  const gridRef = useGridApiRef();
  const [rowCount, setRowCount] = useState(metadata?.totalRecords || 0);

  const columns = useMemo(() => BuildColumns(), [BuildColumns]);

  useEffect(() => {
    if (gridRef && gridRef.current) {
      setGridApiRef(gridRef);
    }
  }, [gridRef, gridRef?.current]);

  useEffect(() => {
    setRowCount((prevRowCount) =>
      metadata?.totalRecords !== undefined ? metadata?.totalRecords : prevRowCount
    );
  }, [metadata?.totalRecords]);

  const handleProcessRowUpdateError = useCallback(
    (_err: Error) => {
      setErrorMessage(t(`screens.${translationKey}.errorUpdating`));
    },
    [setErrorMessage]
  );

  const onClickEditRowTable = useCallback(
    (params: GridRowParams) => {
      if (!isTablet) return;

      gridRef?.current?.startRowEditMode({ id: params.id });
    },
    [isTablet, gridRef]
  );

  const setPagination = useCallback(
    (newPaginationModel: GridPaginationModel) => {
      setPaginationModel(newPaginationModel);
    },
    [setPaginationModel]
  );

  return (
    <DataGridPro
      rows={data || []}
      apiRef={gridRef}
      editMode="row"
      getRowId={(row: T) => row[OBJECT_KEY_ID]}
      columns={columns}
      rowCount={rowCount}
      data-qa-id={`table-${translationKey}`}
      pagination
      paginationMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={setPagination}
      pageSizeOptions={[10, 20, 50]}
      rowHeight={48}
      columnHeaderHeight={60}
      disableColumnMenu
      disableRowSelectionOnClick
      columnVisibilityModel={showColumnsButton ? columnVisibilityModel : undefined}
      slots={{
        noResultsOverlay: NoRows,
        noRowsOverlay: NoRows,
      }}
      processRowUpdate={processRowUpdate}
      onProcessRowUpdateError={handleProcessRowUpdateError}
      onRowClick={onClickEditRowTable}
      onRowEditStart={rowStartEdit}
      disableVirtualization={process.env.NODE_ENV === 'test'}
      loading={isLoading}
      slotProps={{
        pagination: {
          labelRowsPerPage: t('components.table.rowsPerPage'),
          labelDisplayedRows: (paginationInfo) =>
            t('components.table.displayedRows', {
              from: paginationInfo.from,
              to: paginationInfo.to,
              count: paginationInfo.count,
            }),
        },
      }}
      sx={{
        backgroundColor: 'background.paper',
        borderColor: 'grey.23p',
        '& ::-webkit-scrollbar': {
          width: '6px',
          height: '6px',
        },
        '& .MuiDataGrid-virtualScroller': {
          overflowX: 'auto',
        },
        '& ::-webkit-scrollbar-track': {
          backgroundColor: theme?.palette?.grey?.['8p'],
          borderRadius: '10px',
        },
        '& ::-webkit-scrollbar-thumb': {
          borderRadius: '10px',
          backgroundColor: theme?.palette?.grey?.['50p'],
        },
        '& .hideRightSeparator > .MuiDataGrid-columnSeparator': {
          display: 'none',
        },
        '& .MuiDataGrid-columnHeaderTitleContainer': {
          alignItems: 'center',
        },
        '.MuiDataGrid-iconButtonContainer': {
          visibility: 'visible',
        },
        '.MuiTablePagination-selectLabel': {
          color: 'text.secondary',
        },
        '.MuiToolbar-root': {
          color: 'text.secondary',
        },
        '.MuiDataGrid-columnHeader': {
          padding: '0 12px',
          ...(isTablet && {
            padding: '0 8px',
          }),
          '&:first-of-type': {
            paddingLeft: 3,
            ...(!isMediumDevice && {
              paddingLeft: 2,
            }),
          },
          '&:last-child': {
            paddingRight: 3,
            ...(!isMediumDevice && {
              paddingRight: 2,
            }),
          },
        },
        '.MuiDataGrid-cell': {
          padding: '0 12px',
          ...(isTablet && {
            padding: '0 8px',
          }),
          '&:first-of-type': {
            paddingLeft: 3,
            ...(!isMediumDevice && {
              paddingLeft: 2,
            }),
          },
          whiteSpace: 'break-spaces',
          lineHeight: '1',
          '&:has(.Mui-error)': {
            border: '2px solid #D32F2F',
          },
          '&.MuiDataGrid-cell:has(.Mui-focused):has(.Mui-error)': {
            outline: 'none',
          },
        },
        '.MuiDataGrid-cell--editing': {
          '&:first-of-type': {
            input: {
              paddingLeft: 0,
            },
          },
          input: {
            paddingLeft: 1.5,
          },
        },
        '.actionsCell': {
          paddingRight: 2,
          ...(!isMediumDevice && {
            pr: 1,
          }),
        },
        ...((data?.length === 0 || isLoading) && {
          '& .MuiDataGrid-virtualScroller': {
            overflow: 'hidden',
          },
        }),
        '.MuiDataGrid-row:has(.MuiDataGrid-cell--editing .MuiTextField-root)': {
          minHeight: '50px',
          maxHeight: '100px',

          '.MuiDataGrid-cell': {
            minHeight: '50px',
            maxHeight: '100px',
          },
        },
      }}
    />
  );
};

export default DesktopDataGrid;
